import React from 'react'
import { Link } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBell,
  faCircleCheck,
  faCircleXmark,
  faKey,
  faPenToSquare,
  faQrcode,
  faShieldHalved,
  faTrashCan,
  faUser,
  faUserCheck,
  faUsers,
  faUserSlash,
} from '@fortawesome/free-solid-svg-icons'

// Colori avatar deterministici a partire dal nome — stessa persona = stesso
// colore in lista, senza bisogno di state lato server.
const AVATAR_COLORS = [
  '#0f766e', // teal
  '#7c3aed', // violet
  '#db2777', // pink
  '#ea580c', // orange
  '#0369a1', // sky
  '#15803d', // green
  '#b45309', // amber
  '#7e22ce', // purple
  '#1d4ed8', // blue
  '#be123c', // rose
]

function colorFromString(s) {
  const str = s || ''
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function initialsOf(name, fallback) {
  const src = (name || fallback || '').trim()
  if (!src) return '?'
  const parts = src.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function typeLabel(t) {
  if (t === 'admin') return 'Admin'
  if (t === 'producer') return 'Producer'
  if (t === 'single') return 'Singolo'
  return t || 'Utente'
}

function typeIcon(t) {
  if (t === 'admin') return faShieldHalved
  if (t === 'producer') return faUsers
  return faUser
}

// Storicamente il form salvava enabled come boolean false (default
// useState), che mongoose castava a stringa "false". Quindi MOLTI utenti
// legacy hanno enabled === "false" pur non essendo mai stati realmente
// disabilitati. Per evitare falsi positivi consideriamo "disabilitato"
// SOLO il marker esplicito "disabled" — settato unicamente dal nuovo
// toggle admin.
function isUserEnabled(user) {
  return !user || user.enabled !== 'disabled'
}

function ActionButton({
  icon,
  label,
  title,
  onClick,
  variant = 'default',
  disabled = false,
  href = null,
}) {
  const className = `user-action user-action-${variant}` + (disabled ? ' is-disabled' : '')
  const Inner = (
    <>
      <span className="user-action-icon">
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className="user-action-label">{label}</span>
    </>
  )
  if (href && !disabled) {
    return (
      <Link to={href} className={className} title={title} aria-label={title}>
        {Inner}
      </Link>
    )
  }
  return (
    <button
      type="button"
      className={className}
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
    >
      {Inner}
    </button>
  )
}

function UserListItem(props) {
  const u = props.user || {}
  const enabled = isUserEnabled(u)
  const isAdmin = u.type === 'admin'
  // L'admin loggato NON può disabilitare se stesso (o eliminarsi). Le altre
  // azioni che hanno senso solo sui clienti finali (QR di accesso, push,
  // reset password) restano nascoste agli admin.
  const isSelf =
    props.currentUserId && u._id && String(props.currentUserId) === String(u._id)
  const avatarColor = colorFromString(u.name || u.email || u._id)
  const initials = initialsOf(u.name, u.email)

  return (
    <div className={'user-card' + (enabled ? '' : ' user-card-disabled')}>
      <div className="user-card-head">
        <div
          className="user-avatar"
          style={{ backgroundColor: avatarColor }}
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="user-identity">
          <div className="user-name-row">
            <h3 className="user-name">{u.name || 'Senza nome'}</h3>
            <span
              className={
                'user-status-pill user-status-' + (enabled ? 'on' : 'off')
              }
              title={
                enabled
                  ? 'Utente attivo: può ordinare e accedere'
                  : 'Utente disabilitato: non può accedere finché non lo riattivi'
              }
            >
              <FontAwesomeIcon icon={enabled ? faCircleCheck : faCircleXmark} />
              {enabled ? 'Attivo' : 'Disabilitato'}
            </span>
          </div>
          <div className="user-email">{u.email || '—'}</div>
          <div className="user-meta">
            <span className="user-meta-chip" title={`Ruolo: ${typeLabel(u.type)}`}>
              <FontAwesomeIcon icon={typeIcon(u.type)} />
              {typeLabel(u.type)}
            </span>
            {u.customer ? (
              <span className="user-meta-chip" title={`Cliente: ${u.customer}`}>
                {u.customer}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="user-actions">
        {!isSelf && (
          <ActionButton
            icon={enabled ? faUserSlash : faUserCheck}
            label={enabled ? 'Disabilita' : 'Riabilita'}
            title={
              enabled
                ? isAdmin
                  ? "Sospende l'admin. Non potrà più accedere finché non viene riattivato. Operazione reversibile."
                  : "Sospende l'utente. Non potrà più ordinare né effettuare login finché non lo riattivi. Operazione reversibile."
                : "Riabilita l'utente: tornerà a poter accedere all'app."
            }
            variant={enabled ? 'warn' : 'success'}
            onClick={() => props.onToggleEnabled && props.onToggleEnabled(u)}
          />
        )}

        {!isAdmin && props.onShowQr && (
          <ActionButton
            icon={faQrcode}
            label="QR accesso"
            title="Genera un QR di accesso rapido one-time per questo utente. Inviabile via WhatsApp; dopo la prima scansione si invalida da solo."
            onClick={props.onShowQr}
          />
        )}

        {!isAdmin && props.onTestPush && (
          <ActionButton
            icon={faBell}
            label="Test push"
            title="Invia una notifica push di prova ai dispositivi su cui l'utente ha attivato le notifiche."
            onClick={props.onTestPush}
          />
        )}

        <ActionButton
          icon={faKey}
          label="Reset password"
          title="Invia un'email all'utente con un link valido 60 minuti per scegliere una nuova password."
          onClick={props.resetPassword}
        />

        <ActionButton
          icon={faPenToSquare}
          label="Modifica"
          title="Apri la scheda utente per modificare nome, email, ruolo, cliente."
          href={`/users/${u._id}`}
        />

        {!isSelf && (
          <ActionButton
            icon={faTrashCan}
            label="Elimina"
            title="Elimina definitivamente l'utente dal database. Operazione IRREVERSIBILE: richiede conferma testuale."
            variant="danger"
            onClick={() => props.onDelete && props.onDelete(u)}
          />
        )}
      </div>
    </div>
  )
}

export default UserListItem
